import React, { useEffect } from 'react';
import "@principal/design-system/pds-styles.min.css";
import Header from "./component/navigation/Header";
import { Route, Routes, useLocation } from "react-router-dom";
import './App.css';
import PageViewMetric from "./component/navigation/PageViewMetric";
import Employee from "./component/common/Employee";
import Employer from "./component/common/Employer";
import Physician from "./component/common/Physician";
import PartyChoice from "./component/page/PartyChoice";
import LoginChoice from "./component/page/LoginChoice";
import VerifyEmail from "./component/page/VerifyEmail";
import VerifyEmailSent from "./component/page/VerifyEmailSent";
import GuestValidate from "./component/page/GuestValidate";
import GuestRecover from "./component/page/GuestRecover";
import GuestProtectedRoute from "./component/guest/GuestProtectedRoute";
import GuestSessionBanner from "./component/guest/GuestSessionBanner";
import { GuestAuthProvider, useGuestAuth } from "./component/guest/GuestAuthProvider";
import ProductChoice from "./component/ProductChoice";
import Beneficiary from "./component/common/Beneficiary";
import { PdsErrorPage, PdsLayoutContainer, PdsLoadingPage } from "@principal/design-system-react";
import axiosRetry from "axios-retry";
import axios from "axios";
import { ApolloClient, ApolloProvider, from, HttpLink, InMemoryCache } from "@apollo/client";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { UserManager, useAuth } from "oidc-react";
import { convertDatesLink } from "./links/ConvertDatesLink";
import { isMutationOperation } from "@apollo/client/utilities";
import { createNetworkStatusNotifier } from "react-apollo-network-status";
import Footer from "./component/structure/Footer";

const { link: networkStatusLink, useApolloNetworkStatus } = createNetworkStatusNotifier();

export const useGlobalLoadingState: () => boolean = () => {
    const status = useApolloNetworkStatus();
    return (status.numPendingQueries + status.numPendingMutations) > 0;
};

export type AppProps = {
    userManager: UserManager,
}

function App(props: AppProps) {
    return (
        <GuestAuthProvider>
            <AppContent userManager={props.userManager} />
        </GuestAuthProvider>
    );
}

function AppContent(props: AppProps) {
    const { userManager } = props;
    const location = useLocation();
    const oidcAuth = useAuth();
    const isSecureLoggedIn = !!oidcAuth?.userData;
    const { guestAuth, isLoggedIn, silentSignInInitialize } = useGuestAuth();

    axiosRetry(axios, {
        retries: 10,
        retryDelay: axiosRetry.exponentialDelay,
        retryCondition: (error) => {
            if (!error.code || error.response || (error.config?.method === 'post')) {
                return false
            }
            return ['ECONNABORTED', 'ERR_CANCELED', 'ECONNRESET', 'ERR_NETWORK', 'ETIMEDOUT', 'ECONNREFUSED'].includes(error.code)
        },
        shouldResetTimeout: true
    })

    useEffect(() => {
        if (isLoggedIn && !isSecureLoggedIn) {
            silentSignInInitialize();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isSecureLoggedIn]);

    function buildClient() {
        const retryLink = new RetryLink({
            attempts: {
                retryIf: (error, operation) => !!error && !isMutationOperation(operation.query)
            },
        });
        const removeTypenameLink = removeTypenameFromVariables();
        const authLink = setContext(async (_, { headers }) => {
            const oidcToken = await findTokenFor(userManager);
            const token = oidcToken ?? guestAuth?.token ?? null;
            return {
                headers: {
                    ...headers,
                    authorization: token ? `Bearer ${token}` : ''
                },
                isGuestToken: !oidcToken && !!guestAuth?.token
            };
        });
        return new ApolloClient({
            link: from([retryLink, removeTypenameLink, convertDatesLink, authLink, networkStatusLink, new HttpLink({
                uri: (operation) => {
                    const context = operation.getContext();
                    const hasToken = !!context?.headers?.authorization;
                    const isGuestToken = context?.isGuestToken;
                    if (hasToken && !isGuestToken) {
                        return process.env.REACT_APP_FORMS_SERVICE_SECURED_GRAPHQL_URL!;
                    }
                    return process.env.REACT_APP_FORMS_SERVICE_UNSECURED_GRAPHQL_URL!;
                },
                fetch
            })]),
            cache: new InMemoryCache({
                typePolicies: {
                    Fraud: {
                        keyFields: ['state']
                    },
                    Member: {
                        keyFields: ['id']
                    },
                    Activity: {
                        keyFields: ['id']
                    },
                    Policy: {
                        keyFields: ['id']
                    },
                    EmployerPolicy: {
                        keyFields: ['id']
                    },
                    BillGroup: {
                        keyFields: ['policyId', 'id']
                    },
                    EmployerUnit: {
                        keyFields: ['policyId', 'id']
                    },
                    MemberGroupType: {
                        keyFields: ['policyId', 'id']
                    },
                    EmployerMemberGroup: {
                        keyFields: ['policyId', 'id']
                    }
                }
            })
        });
    }

    if (window.location.pathname === '/redirect') {
        return <Routes>
            <Route path={''} element={<PdsLoadingPage />} />
        </Routes>
    }

    return (
        <ApolloProvider client={buildClient()}>
            <div>
                <Header />
                <PdsLayoutContainer>
                    <Routes>
                        {process.env.REACT_APP_MAINTENANCE_TOGGLE === 'true' &&
                            <Route path={''} element={<PdsErrorPage errorCode="503" />} />
                        }
                        <Route path={'/error'} element={<PdsErrorPage />} />
                        <Route path={'/:coverage/:claimId?/employee'} element={
                            <GuestProtectedRoute party="employee"><Employee /></GuestProtectedRoute>
                        } />
                        <Route path={'/:coverage/:claimId?/employer'} element={
                            <GuestProtectedRoute party="employer"><Employer /></GuestProtectedRoute>
                        } />
                        <Route path={'/:coverage/:claimId?/physician'} element={
                            <GuestProtectedRoute party="physician"><Physician /></GuestProtectedRoute>
                        } />
                        <Route path={'/:coverage/:claimId?/beneficiary'} element={
                            <GuestProtectedRoute party="beneficiary"><Beneficiary /></GuestProtectedRoute>
                        } />
                        <Route path={'/:coverage/:party/login'} element={<LoginChoice />} />
                        <Route path={'/:coverage/:party/verify-email'} element={<VerifyEmail />} />
                        <Route path={'/:coverage/:party/verify-email/sent'} element={<VerifyEmailSent />} />
                        <Route path={'/:coverage/:claimId?/:party/guest/validate'} element={<GuestValidate />} />
                        <Route path={'/:coverage/:claimId?/:party/guest/recover'} element={<GuestRecover />} />
                        <Route path={'/:coverage'} element={<PartyChoice />} />
                        <Route path={'/'} element={<ProductChoice />} />
                    </Routes>
                </PdsLayoutContainer>
                <Footer>
                    {['/criticalIllness', '/'].includes(location.pathname) &&
                        <div>Critical Illness is Specified Disease in New York</div>}
                </Footer>
            </div>
            <PageViewMetric />
            {!isSecureLoggedIn && <GuestSessionBanner />}
        </ApolloProvider>
    );
}

async function findTokenFor(userManager: UserManager) {
    const user = await userManager.getUser()
    return user?.access_token;
}

export default App;
